import { Box, visuallyHiddenStyle } from '@chakra-ui/react'
import React, {
  useCallback,
  useEffect,
  useRef,
  useSyncExternalStore,
} from 'react'
import { createPortal } from 'react-dom'

export type AnnouncementPoliteness = 'polite' | 'assertive'

interface Announcement {
  id: number
  message: string
  announcedAt: number
  owner?: symbol
}

/**
 * Screen readers pick a message up as soon as it is inserted, so it only has
 * to stay long enough to survive a busy speech queue. Keeping it shorter than
 * that lets a user browsing with the virtual cursor find a status that no
 * longer applies (e.g. "Marked as unread" after moving to another voicemail).
 */
export const CLEAR_ANNOUNCEMENT_AFTER_MS = 5000
/** The same message and politeness repeated within this window is dropped. */
export const ANNOUNCEMENT_DEDUPE_MS = 1000
/**
 * A message stays in its region at least this long before the next one in the
 * same region replaces it, so quick successive messages (e.g. "Call ended"
 * then the call-feedback prompt) are each read instead of only the last.
 * Messages arriving sooner wait; only the newest waiting message is kept.
 */
export const ANNOUNCEMENT_MIN_DISPLAY_MS = 1000

const POLITENESS_LEVELS: AnnouncementPoliteness[] = ['polite', 'assertive']

type Timer = ReturnType<typeof setTimeout>
type RegionMessages = Record<AnnouncementPoliteness, Announcement | null>

interface PendingAnnouncement {
  message: string
  owner?: symbol
}

// Each region has its own message, waiting message and timers, so a polite
// message can't replace an assertive one the screen reader hasn't read yet.
let current: RegionMessages = { polite: null, assertive: null }
const pending: Record<AnnouncementPoliteness, PendingAnnouncement | null> = {
  polite: null,
  assertive: null,
}
let nextAnnouncementId = 0
const clearTimers: Record<AnnouncementPoliteness, Timer | null> = {
  polite: null,
  assertive: null,
}
const pendingTimers: Record<AnnouncementPoliteness, Timer | null> = {
  polite: null,
  assertive: null,
}
let mountedRegions = 0
const listeners = new Set<() => void>()

const setRegion = (
  politeness: AnnouncementPoliteness,
  announcement: Announcement | null,
) => {
  current = { ...current, [politeness]: announcement }
  listeners.forEach((listener) => listener())
}

const cancelTimer = (
  timers: Record<AnnouncementPoliteness, Timer | null>,
  politeness: AnnouncementPoliteness,
) => {
  const timer = timers[politeness]
  if (timer) {
    clearTimeout(timer)
    timers[politeness] = null
  }
}

const show = (
  politeness: AnnouncementPoliteness,
  { message, owner }: PendingAnnouncement,
) => {
  cancelTimer(clearTimers, politeness)
  nextAnnouncementId += 1
  setRegion(politeness, {
    id: nextAnnouncementId,
    message,
    announcedAt: Date.now(),
    owner,
  })
  clearTimers[politeness] = setTimeout(
    () => clearRegion(politeness),
    CLEAR_ANNOUNCEMENT_AFTER_MS,
  )
}

const showPending = (politeness: AnnouncementPoliteness) => {
  pendingTimers[politeness] = null
  const next = pending[politeness]
  pending[politeness] = null
  if (next) show(politeness, next)
}

const clearRegion = (politeness: AnnouncementPoliteness, owner?: symbol) => {
  if (!owner || pending[politeness]?.owner === owner) {
    cancelTimer(pendingTimers, politeness)
    pending[politeness] = null
  }
  if (owner && current[politeness]?.owner !== owner) return
  cancelTimer(clearTimers, politeness)
  if (current[politeness]) setRegion(politeness, null)
}

/** Clears both regions, or only the messages `owner` sent. */
const clearAnnouncements = (owner?: symbol) =>
  POLITENESS_LEVELS.forEach((politeness) => clearRegion(politeness, owner))

const announceAs = (
  owner: symbol | undefined,
  message: string,
  politeness: AnnouncementPoliteness,
) => {
  // Callers run this inside error handlers, so it must never throw.
  if (typeof message !== 'string' || !message) return
  const now = Date.now()
  const shown = current[politeness]
  if (
    pending[politeness]?.message === message ||
    (shown?.message === message &&
      now - shown.announcedAt < ANNOUNCEMENT_DEDUPE_MS)
  ) {
    return
  }

  const shownFor = shown ? now - shown.announcedAt : Infinity
  if (shownFor >= ANNOUNCEMENT_MIN_DISPLAY_MS) {
    show(politeness, { message, owner })
    return
  }

  pending[politeness] = { message, owner }
  if (!pendingTimers[politeness]) {
    pendingTimers[politeness] = setTimeout(
      () => showPending(politeness),
      ANNOUNCEMENT_MIN_DISPLAY_MS - shownFor,
    )
  }
}

/**
 * Sends a message to screen readers through the window's `LiveAnnouncer`.
 * Usable outside React. Use `assertive` only for failures that need immediate
 * attention. Identical messages within `ANNOUNCEMENT_DEDUPE_MS` are announced
 * once; the same text is announced again once it has cleared. A message sent
 * less than `ANNOUNCEMENT_MIN_DISPLAY_MS` after the previous one in the same
 * region waits its turn instead of replacing it.
 */
export const announce = (
  message: string,
  politeness: AnnouncementPoliteness = 'polite',
) => announceAs(undefined, message, politeness)

interface UseAnnounceOptions {
  /**
   * Clears this caller's message when the value changes or the caller
   * unmounts, so a status about the previous selection isn't re-read.
   */
  clearWhenChanged?: unknown
}

/** React access to `announce`, with optional clearing tied to a value. */
export const useAnnounce = (options?: UseAnnounceOptions) => {
  const ownerRef = useRef<symbol | null>(null)
  if (!ownerRef.current) ownerRef.current = Symbol('announcer')
  const clearsOnChange = options !== undefined
  const clearWhenChanged = options?.clearWhenChanged

  useEffect(() => {
    if (!clearsOnChange) return
    const owner = ownerRef.current ?? undefined
    return () => clearAnnouncements(owner)
  }, [clearsOnChange, clearWhenChanged])

  return useCallback(
    (message: string, politeness: AnnouncementPoliteness = 'polite') =>
      announceAs(ownerRef.current ?? undefined, message, politeness),
    [],
  )
}

const subscribe = (listener: () => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

const getSnapshot = () => current

/**
 * Renders the window's one polite and one assertive live region. Mount once
 * per window (the main app and the call-pop window). Each message is a new
 * node so repeated text is still announced, and it clears after
 * `CLEAR_ANNOUNCEMENT_AFTER_MS` so it isn't found when browsing.
 *
 * The regions are portalled to a `[data-live-announcer]` child of `<body>`.
 * Modal dialogs (zag `ariaHidden`, @zag-js/aria-hidden `isIgnoredNode`) set
 * `aria-hidden` on everything outside themselves but skip `[data-live-announcer]`
 * body children, so announcements made from a dialog still reach screen readers.
 */
export const LiveAnnouncer = () => {
  const { polite, assertive } = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getSnapshot,
  )

  useEffect(() => {
    mountedRegions += 1
    return () => {
      mountedRegions -= 1
      if (mountedRegions === 0) clearAnnouncements()
    }
  }, [])

  if (typeof document === 'undefined') return null

  return createPortal(
    <div data-live-announcer="">
      <Box
        css={visuallyHiddenStyle}
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {polite && <span key={polite.id}>{polite.message}</span>}
      </Box>
      <Box
        css={visuallyHiddenStyle}
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
      >
        {assertive && <span key={assertive.id}>{assertive.message}</span>}
      </Box>
    </div>,
    document.body,
  )
}
